import app from 'flarum/forum/app';
import Translator from 'flarum/common/Translator';
import extractText from 'flarum/common/utils/extractText';

/**
 * Meertaligheid van het publieke deel (clubarchief, artiesten, figuren, The Dark Pages).
 *
 * De taal volgt uit het adres: /fr/... en /en/..., anders Nederlands. Flarum zelf laadt
 * maar één taal (die van het forum, Nederlands); de server geeft daarom de teksten van
 * deze extensie in alle talen mee (payload lcI18n, uit locale/*.yml) en we gebruiken per
 * taal een eigen Flarum-Translator. Het forum en het adminpaneel blijven Nederlands.
 */

const PREFIX = 'legendary-clubs.forum.';
const translators = {};
const warned = {};

function config() {
  return app.data.lcI18n || { default: 'nl', cookie: 'lc_lang', locales: { nl: { name: 'Nederlands', intl: 'nl-BE' } }, routes: {}, strings: {} };
}

export function defaultLocale() {
  return config().default;
}

export function locales() {
  return Object.keys(config().locales);
}

export function localeInfo(locale) {
  return config().locales[locale] || {};
}

function basePath() {
  return (app.forum && app.forum.attribute('basePath')) || '';
}

/** Huidig pad zonder basePath, bv. "/fr/clubs/boccaccio". */
function currentPath() {
  let path = window.location.pathname;
  try {
    path = m.route.get() || path;
  } catch (e) {
    // router nog niet klaar
  }
  const base = basePath();
  return base && path.indexOf(base) === 0 ? path.slice(base.length) || '/' : path;
}

/** Taal van een pad: "fr" voor /fr en /fr/..., anders de standaardtaal. */
export function localeOfPath(path) {
  const seg = (path || '').split(/[/?#]/)[1];
  return seg && seg !== defaultLocale() && config().locales[seg] ? seg : defaultLocale();
}

/** De taal van de pagina die nu getoond wordt. */
export function currentLocale() {
  return localeOfPath(currentPath());
}

/**
 * Staan we op een pagina van het publieke deel (en niet op het forum)? Op basis van het
 * adres, zodat het ook klopt bij de allereerste render, vóór Flarum de route kent:
 * alles onder /fr/ en /en/, de homepage en de Nederlandse paden uit de route-tabel.
 */
export function isPublicPage() {
  const path = currentPath();
  if (localeOfPath(path) !== defaultLocale() || path === '/') return true;
  const routes = config().routes;
  const bases = ['lc.clubs', 'lc.artists', 'lc.figures', 'lc.dark'].map((name) => routes[name] && routes[name][defaultLocale()]).filter(Boolean);
  bases.push('/figuren'); // oude adressen van /faces
  return bases.some((base) => path === base || /^[/?#]/.test(path.slice(base.length)) && path.indexOf(base) === 0);
}

function translator(locale) {
  if (!translators[locale]) {
    const strings = config().strings;
    const t = new Translator();
    t.setLocale(locale);
    // Nederlands als vangnet voor sleutels die (nog) niet vertaald zijn
    t.addTranslations(Object.assign({}, strings[defaultLocale()] || {}, strings[locale] || {}));
    translators[locale] = t;
  }
  return translators[locale];
}

/** Vertaling in de taal van de pagina (of `locale`). Geeft Mithril-inhoud terug, zoals app.translator.trans. */
export function t(key, params, locale) {
  const l = locale || currentLocale();
  const id = PREFIX + key;
  if (l !== defaultLocale() && !warned[l + id] && !((config().strings[l] || {})[id])) {
    warned[l + id] = true;
    console.warn(`[legendary-clubs] vertaling ontbreekt (${l}): ${id}`);
  }
  return translator(l).trans(id, params || {});
}

/** Zoals t(), maar als platte tekst (voor attributen zoals placeholder en aria-label). */
export function tt(key, params, locale) {
  return extractText(t(key, params, locale));
}

/** Vertaling van een gegevenswaarde (provincie, clubtype, ...), of de waarde zelf als die onbekend is. */
export function label(group, value, locale) {
  if (!value) return value;
  const id = `${PREFIX}${group}.${value}`;
  const strings = config().strings;
  return (strings[locale || currentLocale()] || {})[id] || (strings[defaultLocale()] || {})[id] || value;
}

/** Naam van de route in een taal: "lc.club" + "fr" => "lc.club.fr". */
export function routeName(name, locale) {
  const l = locale || currentLocale();
  // De Nederlandse homepage is Flarums default route (/clubs, getoond als "/").
  if (name === 'lc.home' && l === defaultLocale()) return 'lc.clubs';
  return l === defaultLocale() ? name : `${name}.${l}`;
}

/** app.route() in de taal van de pagina (of `locale`). */
export function route(name, params, locale) {
  return app.route(routeName(name, locale), params || {});
}

/** De Nederlandse routenaam bij een (vertaalde) routenaam: "lc.club.fr" => "lc.club". */
function baseRouteName(name) {
  const l = name.split('.').pop();
  return config().locales[l] && l !== defaultLocale() ? name.slice(0, -(l.length + 1)) : name;
}

/** Adres van de huidige pagina in een andere taal (zelfde club, artiest, filter, ...). */
export function alternateUrl(locale) {
  const name = app.current && app.current.get('routeName');
  if (!name || name.indexOf('lc.') !== 0) return route('lc.home', {}, locale);
  let base = baseRouteName(name);
  if (base === 'lc.figures.old') base = 'lc.figures';
  if (base === 'lc.figure.old') base = 'lc.figure';
  const params = Object.assign({}, m.route.param());
  return route(base, params, locale);
}

/** Registreert de routes van alle talen (paden komen uit I18n\Locale::ROUTES). */
export function registerRoutes(components) {
  const routes = config().routes;
  Object.keys(routes).forEach((name) => {
    Object.keys(routes[name]).forEach((locale) => {
      if (locale === defaultLocale()) return;
      const path = routes[name][locale].replace(/\{(\w+)\}/g, ':$1');
      app.routes[routeName(name, locale)] = { path, component: components[name] };
    });
  });
}

// ------------------------------------------------------------------ voorkeur

/** De bewust gekozen taal (cookie), voor links vanuit het forum naar het publieke deel. */
export function preferredLocale() {
  const match = document.cookie.match(new RegExp('(?:^|; )' + config().cookie + '=([a-z]{2})'));
  return match && config().locales[match[1]] ? match[1] : defaultLocale();
}

export function rememberLocale(locale) {
  const name = config().cookie;
  // Functionele cookie zonder persoonsgegevens: enkel de taalcode.
  document.cookie =
    locale === defaultLocale()
      ? `${name}=; path=/; max-age=0; SameSite=Lax`
      : `${name}=${locale}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

/** Taal voor links naar het publieke deel: die van de pagina, of op het forum de gekozen voorkeur. */
export function navLocale() {
  return isPublicPage() ? currentLocale() : preferredLocale();
}

// ------------------------------------------------------------------ gegevens

const TYPES = ['lc-clubs', 'lc-artists', 'lc-figures', 'lc-dark-stories'];
let dataLocale = null;
const resetters = [];

/** Iets dat bij een taalwissel opnieuw moet laden (bv. de gecachte lijst van Dark Pages). */
export function onLocaleChange(fn) {
  resetters.push(fn);
}

/**
 * Aan het begin van elke pagina van het publieke deel: zet <html lang> en gooit de
 * modellen van een andere taal uit de store, zodat er niets in de verkeerde taal blijft
 * staan na navigatie binnen de app.
 */
export function syncLocale() {
  const locale = currentLocale();
  document.documentElement.lang = locale;
  if (dataLocale !== null && dataLocale !== locale) {
    TYPES.forEach((type) => delete app.store.data[type]);
    resetters.forEach((fn) => fn());
  }
  dataLocale = locale;
  return locale;
}

/** app.store.find met de taal van de pagina (?lang=fr), zodat de API de vertaalde inhoud geeft. */
export function find(type, id, params) {
  const locale = currentLocale();
  const extra = locale === defaultLocale() ? {} : { lang: locale };
  if (typeof id === 'object' || id === undefined) return app.store.find(type, Object.assign({}, id || {}, extra));
  return app.store.find(type, id, Object.assign({}, params || {}, extra));
}

// ------------------------------------------------------------------ opmaak

/** Getal in de notatie van de taal: 68.687 / 68 687 / 68,687. */
export function formatNumber(n) {
  return (n || 0).toLocaleString(localeInfo(currentLocale()).intl || 'nl-BE');
}

/** Intl-locale voor sorteren en datums. */
export function intlLocale() {
  return localeInfo(currentLocale()).intl || 'nl-BE';
}

/**
 * lang-attribuut voor inhoud die in het Nederlands blijft op een Franse of Engelse pagina
 * (nog niet vertaald), zodat schermlezers ze correct uitspreken. Anders undefined.
 */
export function contentLang(model) {
  const status = model && model.translation ? model.translation() : null;
  return currentLocale() !== defaultLocale() && status && status !== 'translated' && status !== 'source' ? defaultLocale() : undefined;
}

/**
 * Nederlands pad van het publieke deel => hetzelfde in een andere taal
 * ("/artiesten?rol=dj" => "/fr/artistes?rol=dj"). Andere paden blijven ongewijzigd.
 */
export function localizePath(path, locale) {
  const routes = config().routes;
  if (locale === defaultLocale()) return path;
  for (const name of ['lc.clubs', 'lc.artists', 'lc.figures', 'lc.dark']) {
    const nl = routes[name] && routes[name][defaultLocale()];
    if (!nl) continue;
    const rest = path.slice(nl.length);
    if (path.indexOf(nl) === 0 && (rest === '' || /^[/?#]/.test(rest))) return routes[name][locale] + rest;
  }
  return path;
}
